// Mes indisponibilités (et celles de mes enfants) — D-009, D-114.
//  - une PÉRIODE (dates) avec une case « médical » : jamais la nature du problème ;
//  - ou une règle CHAQUE SEMAINE : un ou plusieurs jours, toute la journée ou une plage horaire, jusqu'à une date
//    ou toute la saison ; l'effet est dit avant d'enregistrer (maquettes i01, i02) ;
//  - la liste « En cours et à venir », avec la prochaine séance concernée par chaque règle (i03).
// La fiche d'ajout sert aussi au coach, depuis la page de l'équipe (openUnavSheet).
import './indispos.css';
import {
  addRule, addUnavailability, deleteUnavailability, removeRule, toDomainRule, updateRule, type RuleDraft, type RuleRow, type UnavailabilityRow,
} from '../lib/api';
import { localDate, parseLocalDate } from '../domain/schedule';
import { agree, displayName, type TeamGender } from '../domain/labels';
import {
  clock, isoDow, ruleCurrent, ruleEffect, ruleSentence, ruleText, toMin, untilText, type MatchSlot, type OccInfo, type WeekSlot,
} from '../domain/availability';
import { children, genderOf, teamOf, type Ctx } from './context';
import { isMinorPerson } from '../domain/minors';
import { shortDate, time } from './format';
import { $, $$, armed, html, mount, openSheet, toast, type Html } from './dom';
import { loadAgenda, type AgendaItem } from './agenda';
import { occOf } from './availability-data';

/** Une personne pour qui poser une indisponibilité (moi, mon enfant, une joueuse de mon équipe). */
export interface UnavPerson { id: string; name: string; tag: string | null; gender: TeamGender; minor: boolean; self: boolean }

export interface UnavSheetOpts {
  people: UnavPerson[];
  /** La personne choisie d'abord. */
  personId?: string;
  /** Les séances et matchs à venir (aperçu de l'effet), qui y est convoquée, et chaque occurrence vue par la règle. */
  items: AgendaItem[];
  convoked: (it: AgendaItem, personId: string) => boolean;
  occ: (it: AgendaItem) => OccInfo;
  /** Une règle à modifier (« Modifier »). */
  rule?: RuleRow;
  /** Le coach pose l'indisponibilité : le staff n'est pas prévenu (c'est lui). */
  staff?: boolean;
  onDone: () => void;
}

const DAY_CHIPS = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];
const minOf = (d: Date) => d.getHours() * 60 + d.getMinutes();
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** Une note telle qu'écrite, en minuscule au milieu d'une ligne (« cours du soir »). */
const inline = (s: string) => (s.length > 1 && s[1] === s[1]!.toLowerCase() ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/** La fiche « Ajouter une indisponibilité » / « Prévenir le coach de Kenza » / « Modifier la règle ». */
export function openUnavSheet(o: UnavSheetOpts): void {
  const today = localDate(new Date());
  let pid = o.personId ?? o.rule?.person_id ?? o.people[0]?.id ?? '';
  const person = () => o.people.find(p => p.id === pid) ?? o.people[0]!;
  const title = () => (o.rule ? 'Modifier la règle' : !person().self && !o.staff ? `Prévenir le coach de ${person().name}` : 'Ajouter une indisponibilité');
  const r0 = o.rule ? toDomainRule(o.rule) : null;
  let kind: 'period' | 'week' = o.rule ? 'week' : 'period';
  let days = new Set<number>(r0?.weekdays ?? []);
  let mode: 'day' | 'from' | 'until' = r0?.fromTime ? 'from' : r0?.toTime ? 'until' : 'day';
  const chips = o.people.length > 1 && o.people.length <= 4 && !o.rule;
  const select = o.people.length > 4 && !o.rule;
  const body = html`
    <form class="form ix-sheet" id="f-ix" novalidate>
      ${chips ? html`<div class="field"><span>Pour</span><div class="ix-for" role="group" aria-label="Pour">${o.people.map(p => html`
        <button type="button" class="chip-toggle chip-sm" data-for="${p.id}" aria-pressed="${String(p.id === pid)}">${p.name}${p.tag ? ` · ${p.tag}` : ''}</button>`)}</div></div>` : ''}
      ${select ? html`<label class="field"><span>Pour</span><select name="person">${o.people.map(p => html`<option value="${p.id}" ${p.id === pid ? 'selected' : ''}>${p.name}</option>`)}</select></label>` : ''}
      ${o.rule ? '' : html`<div class="seg" role="radiogroup" aria-label="Type" data-guide="indispo-kind">
        <button type="button" data-kind="period" aria-pressed="${String(kind === 'period')}">Une période</button>
        <button type="button" data-kind="week" aria-pressed="${String(kind === 'week')}">Chaque semaine</button></div>`}
      <div class="ix-week-f ix-sheet">
        <div class="field"><span>Les jours</span><div class="ix-days" role="group" aria-label="Les jours">${DAY_CHIPS.map((d, i) => html`
          <button type="button" class="chip-toggle chip-sm" data-day="${i + 1}" aria-pressed="${String(days.has(i + 1))}">${d}</button>`)}</div></div>
        <div class="field"><span>Quand</span>
          <div class="seg ix-when" role="radiogroup" aria-label="Quand">
            <button type="button" data-mode="day" aria-pressed="${String(mode === 'day')}">Toute la journée</button>
            <button type="button" data-mode="from" aria-pressed="${String(mode === 'from')}">À partir de</button>
            <button type="button" data-mode="until" aria-pressed="${String(mode === 'until')}">Jusqu’à</button></div>
          <input class="ix-time" type="time" name="at" step="300" value="${r0?.fromTime ?? r0?.toTime ?? '18:00'}" aria-label="Heure"></div>
        <label class="field"><span>Jusqu’au (facultatif)</span><input type="date" name="until" min="${today}" value="${o.rule?.until_on ?? ''}"><small class="ix-help">Sans date : toute la saison.</small></label>
      </div>
      <div class="ix-period-f ix-sheet">
        <div class="row-2">
          <label class="field"><span>Du</span><input type="date" name="from" value="${today}" min="${today}"></label>
          <label class="field"><span>Au</span><input type="date" name="to" value="${today}" min="${today}"></label>
        </div>
        <label class="check"><input type="checkbox" name="medical"><span><strong>Raison médicale</strong><br><span class="muted">Seule cette case est enregistrée, jamais la nature du problème.</span></span></label>
      </div>
      <label class="field"><span>Note pour le coach (facultatif)</span><input name="note" maxlength="80" value="${o.rule?.note ?? ''}" placeholder="ex. cours du soir, stage, voyage scolaire…"></label>
      <p class="ix-preview" aria-live="polite"></p>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit" data-guide="indispo-save"></button>
      ${o.rule ? html`<button type="button" class="btn btn-danger-ghost" id="ix-remove">Retirer cette règle</button>` : ''}
    </form>`;
  openSheet(title(), body, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-ix')!;
    const msg = $(f, '.form-msg')!, preview = $(f, '.ix-preview')!, submit = $<HTMLButtonElement>(f, '[type=submit]')!;
    const at = $<HTMLInputElement>(f, '[name=at]')!, until = $<HTMLInputElement>(f, '[name=until]')!;
    const from = $<HTMLInputElement>(f, '[name=from]')!, to = $<HTMLInputElement>(f, '[name=to]')!;
    const note = $<HTMLInputElement>(f, '[name=note]')!;
    const draft = (): RuleDraft => ({
      weekdays: [...days].sort((a, b) => a - b), from: mode === 'from' ? at.value || null : null, to: mode === 'until' ? at.value || null : null,
      until: until.value || null, note: note.value.trim() || null,
    });
    const refresh = () => {
      const p = person();
      $(sheet, '.sheet-head h3')!.textContent = title();
      $(sheet, '.sheet')?.setAttribute('aria-label', title());
      $(f, '.ix-week-f')!.hidden = kind !== 'week';
      $(f, '.ix-period-f')!.hidden = kind !== 'period';
      at.hidden = mode === 'day';
      note.maxLength = kind === 'week' ? 80 : 200;
      submit.textContent = o.rule ? 'Enregistrer' : !p.self && !o.staff ? `Ajouter pour ${p.name}` : 'Ajouter';
      const mine = o.items.filter(it => o.convoked(it, p.id));
      const absent = agree(p.gender, 'absent', 'absente');
      const off = p.self ? `${agree(p.gender, 'Absent', 'Absente')} d’office` : `${p.name} sera ${absent} d’office`;
      if (kind === 'week') {
        const d = draft();
        if (!d.weekdays.length) { preview.textContent = 'Choisis au moins un jour.'; return; }
        const trainings: WeekSlot[] = [...new Map(mine.filter(it => it.ev.kind !== 'match').map(it => {
          const dow = isoDow(it.occ.date), m = minOf(it.occ.start);
          return [`${dow}-${m}`, { dow, startMin: m }] as const;
        })).values()];
        const matches: MatchSlot[] = mine.filter(it => it.ev.kind === 'match' && (!d.until || it.occ.date <= d.until)).map(it => ({
          occ: o.occ(it), label: `du ${parseLocalDate(it.occ.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' })} (${clock(minOf(it.occ.start))})`,
        }));
        mount(preview, html`<b>${ruleSentence({ weekdays: d.weekdays, fromTime: d.from, toTime: d.to, untilOn: d.until })}</b> ${ruleEffect(
          { weekdays: d.weekdays, fromTime: d.from, toTime: d.to }, trainings, matches, { off, offLower: `${absent} d’office`, late: 'sans échauffement' })}`);
      } else {
        const a = from.value, b = to.value;
        if (!a || !b || b < a) { preview.textContent = 'Vérifie les dates.'; return; }
        const n = mine.filter(it => it.occ.date >= a && it.occ.date <= b).length;
        const dates = a === b ? `Le ${shortDate(a)}.` : `Du ${shortDate(a)} au ${shortDate(b)}.`;
        mount(preview, html`<b>${dates}</b> ${n ? `${off} ${n > 1 ? `aux ${n} séances prévues` : 'à la séance prévue'} sur ces dates.` : 'Aucune séance prévue sur ces dates pour l’instant.'}`);
      }
    };
    for (const b of $$<HTMLButtonElement>(f, '[data-for]')) b.addEventListener('click', () => {
      pid = b.dataset.for!; $$(f, '[data-for]').forEach(x => x.setAttribute('aria-pressed', String(x === b))); refresh();
    });
    $<HTMLSelectElement>(f, '[name=person]')?.addEventListener('change', e => { pid = (e.target as HTMLSelectElement).value; refresh(); });
    for (const b of $$<HTMLButtonElement>(f, '[data-kind]')) b.addEventListener('click', () => {
      kind = b.dataset.kind as typeof kind; $$(f, '[data-kind]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      if (kind === 'week' && !days.size) {   // pour commencer : le jour de sa prochaine séance
        const next = o.items.find(it => o.convoked(it, pid) && it.ev.kind === 'training');
        if (next) { days = new Set([isoDow(next.occ.date)]); $$(f, '[data-day]').forEach(x => x.setAttribute('aria-pressed', String(days.has(Number(x.dataset.day))))); }
      }
      refresh();
    });
    for (const b of $$<HTMLButtonElement>(f, '[data-day]')) b.addEventListener('click', () => {
      const d = Number(b.dataset.day); if (days.has(d)) days.delete(d); else days.add(d);
      b.setAttribute('aria-pressed', String(days.has(d))); refresh();
    });
    for (const b of $$<HTMLButtonElement>(f, '[data-mode]')) b.addEventListener('click', () => {
      mode = b.dataset.mode as typeof mode; $$(f, '[data-mode]').forEach(x => x.setAttribute('aria-pressed', String(x === b))); refresh();
    });
    from.addEventListener('change', () => { to.min = from.value; if (to.value < from.value) to.value = from.value; refresh(); });
    for (const x of [at, until, to, note]) x.addEventListener('input', refresh);
    refresh();
    $(f, '#ix-remove')?.addEventListener('click', async (e) => {
      const b = e.currentTarget as HTMLButtonElement;
      if (!armed(b, 'Confirmer : retirer la règle')) return;
      try { await removeRule(o.rule!.id); close(); toast('Règle retirée'); o.onDone(); } catch { msg.textContent = 'Impossible pour le moment. Réessaie.'; }
    });
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      msg.textContent = '';
      const p = person();
      try {
        if (kind === 'week') {
          const d = draft();
          if (!d.weekdays.length) { msg.textContent = 'Choisis au moins un jour.'; return; }
          if (mode !== 'day' && !at.value) { msg.textContent = 'Indique l’heure.'; return; }
          if (mode === 'from' && toMin(at.value) === 0) { msg.textContent = 'À partir de minuit : choisis plutôt « Toute la journée ».'; return; }
          if (d.until && d.until < today) { msg.textContent = 'La date de fin est passée.'; return; }
          if (o.rule) { await updateRule(o.rule.id, d); toast('Règle modifiée'); }
          else { await addRule(p.id, d); toast(o.staff ? 'Règle ajoutée' : 'Règle ajoutée : le coach est prévenu'); }
        } else {
          if (!from.value || !to.value || to.value < from.value) { msg.textContent = 'Vérifie les dates.'; return; }
          const fd = new FormData(f);
          await addUnavailability(p.id, from.value, to.value, fd.get('medical') === 'on', String(fd.get('note') ?? ''));
          toast(o.staff ? 'Indisponibilité ajoutée' : 'Indisponibilité ajoutée : le coach est prévenu');
        }
        close(); o.onDone();
      } catch { msg.textContent = 'Enregistrement impossible. Réessaie.'; }
    });
  });
}

/** La prochaine séance où la règle compte (sans réponse de sa part) : « entraînement du mercredi 4 nov., 19h00, absente d’office ». */
export function nextConcerned(items: AgendaItem[], rule: RuleRow, g: TeamGender): Html | null {
  for (const it of items) {
    const m = it.mine.find(x => x.personId === rule.person_id);
    if (!m || m.verdict?.source !== 'rule' || m.verdict.ref !== rule.id) continue;
    if (m.presence !== 'unavailable' && !(m.presence === 'expected' && m.verdict.state === 'late')) continue;
    const what = it.ev.kind === 'match' ? 'match' : it.ev.kind === 'training' ? 'entraînement' : it.ev.title.toLowerCase();
    const day = parseLocalDate(it.occ.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' });
    return html`${what} du <b>${day}, ${time(it.occ.start)}</b>, ${m.verdict.state === 'late' ? 'sans échauffement' : `${agree(g, 'absent', 'absente')} d’office`}`;
  }
  return null;
}

/** « 21 au 23 déc. », « 30 oct. au 2 nov. » */
export function periodRange(a: string, b: string): string {
  const da = parseLocalDate(a), db = parseLocalDate(b);
  return da.getMonth() === db.getMonth() && da.getFullYear() === db.getFullYear() ? `${da.getDate()} au ${shortDate(b)}` : `${shortDate(a)} au ${shortDate(b)}`;
}

export async function renderUnavailability(root: HTMLElement, ctx: Ctx): Promise<void> {
  const today = localDate(new Date());
  const ag = await loadAgenda(ctx, 60);
  const occ = (it: AgendaItem) => occOf(it.ev, it.occ, it.match, ag.leads.get(teamOf(ctx, it.ev.team_id)?.club_id ?? ctx.club?.id ?? '') ?? { arrive: 60, home: 45 });
  const ids = new Set(ctx.people.map(p => p.id));
  const periods = ag.avail.periodRows.filter(u => ids.has(u.person_id) && u.ends_on >= today);
  const rules = ag.avail.ruleRows.filter(r => ids.has(r.person_id) && ruleCurrent(toDomainRule(r), today));
  const many = ctx.people.length > 1;
  const nameOf = (pid: string) => { const p = ctx.people.find(x => x.id === pid); return p ? displayName(p) : ''; };
  // Un parent agit pour son enfant (D-103) : le prénom de l'enfant, pas « toi (ou tes parents) ».
  const kids = children(ctx);
  const onlyKid = !many && kids.length === 1 ? kids[0]! : null;
  const who = kids.length === ctx.people.length ? (onlyKid ? `Seuls le staff de ${displayName(onlyKid)} et toi la voient` : 'Seuls leur staff et toi la voient')
    : kids.length ? 'Seuls le staff et la famille la voient' : 'Seuls le staff et toi (ou tes parents) la voient';
  const come = onlyKid ? `${displayName(onlyKid)} peut toujours venir` : kids.length && kids.length === ctx.people.length ? 'Ils peuvent toujours venir' : 'Tu peux toujours venir';
  const people: UnavPerson[] = ctx.people.map(p => {
    const team = ctx.memberships.filter(m => m.person_id === p.id).map(m => teamOf(ctx, m.team_id)).find(Boolean);
    return { id: p.id, name: displayName(p), tag: team?.category ?? null, gender: genderOf(ctx, p.id), minor: isMinorPerson(p), self: p.user_id === ctx.uid };
  });

  const ruleItem = (r: RuleRow) => {
    const d = toDomainRule(r);
    const next = nextConcerned(ag.items, r, genderOf(ctx, r.person_id));
    return html`
    <li class="un-row ix-rule">
      <div class="un-dates">${many ? `${nameOf(r.person_id)} · ${ruleText(d)}` : cap(ruleText(d))} <span class="chip ix-week">chaque semaine</span></div>
      <div class="un-meta"><span>${untilText(d)}</span>${r.note ? html`<span class="muted">${inline(r.note)}</span>` : ''}</div>
      <button type="button" class="btn btn-ghost btn-sm" data-edit="${r.id}">Modifier</button>
      ${next ? html`<p class="ix-next">Prochaine séance concernée : ${next}.</p>` : ''}
    </li>`;
  };
  const periodItem = (u: UnavailabilityRow) => html`
    <li class="un-row">
      <div class="un-dates">
        ${many ? `${nameOf(u.person_id)} · ` : ''}${u.starts_on === u.ends_on ? `Le ${shortDate(u.starts_on)}` : `Du ${periodRange(u.starts_on, u.ends_on)}`}
        ${u.starts_on <= today ? html` <span class="chip chip-mute">en cours</span>` : ''}
      </div>
      <div class="un-meta">
        ${u.is_medical ? html`<span class="tag-med">médical</span>` : ''}
        ${u.note ? html`<span class="muted">${inline(u.note)}</span>` : ''}
      </div>
      <button type="button" class="btn btn-ghost btn-sm" data-del="${u.id}">Retirer</button>
    </li>`;

  const back = kids.length && kids.length === ctx.people.length ? html`<a class="back" href="#/enfants" data-back>‹ Enfants</a>` : html`<a class="back" href="#/moi" data-back>‹ Moi</a>`;
  mount(root, html`
    <section class="screen">
      ${back}
      <header class="screen-head">
        <p class="eyebrow">Absences prévues${onlyKid ? ` · ${displayName(onlyKid)}` : ''}</p>
        <h2>Indisponibilités</h2>
      </header>
      ${ctx.people.length ? html`<button type="button" class="btn btn-primary" id="ix-add" data-guide="indispo-add">+ Ajouter une indisponibilité</button>`
        : html`<div class="empty"><p>Aucune fiche joueur rattachée à ce compte.</p></div>`}
      <section class="block" data-guide="indispo-list">
        <h3>En cours et à venir</h3>
        ${rules.length || periods.length ? html`<ul class="un-list">${rules.map(ruleItem)}${periods.map(periodItem)}</ul>`
          : html`<p class="muted">Aucune indisponibilité prévue. Une période (blessure, vacances) ou une règle chaque semaine (« le mercredi à partir de 18 h ») : l’absence est notée d’office.</p>`}
      </section>
      <p class="hint ix-list-hint">${come} : « Je viens quand même » sur la séance, la règle reste. ${who}, jamais le reste de l’équipe.</p>
    </section>`);

  const reload = () => renderUnavailability(root, ctx);
  const convoked = (it: AgendaItem, pid: string) => it.mine.some(m => m.personId === pid);
  const opts = { people, items: ag.items, convoked, occ, onDone: () => void reload() };
  $(root, '#ix-add')?.addEventListener('click', () => openUnavSheet(opts));
  for (const b of $$<HTMLButtonElement>(root, '[data-edit]')) b.addEventListener('click', () => {
    const rule = rules.find(r => r.id === b.dataset.edit);
    if (rule) openUnavSheet({ ...opts, rule });
  });
  for (const b of $$<HTMLButtonElement>(root, '[data-del]')) {
    b.addEventListener('click', async () => {
      try { await deleteUnavailability(b.dataset.del!); await reload(); }
      catch { toast('Suppression impossible.', 'ko'); }
    });
  }
}
